import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Field } from "../../components/common";
import { CloverLogo } from "../../components/CloverLogo";
import { usePortalAuth } from "../../context/PortalAuthContext";
import { errMsg } from "../../lib/api";

export default function PortalLogin() {
  const { me, login } = usePortalAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("hp");
  const [identifier, setIdentifier] = useState("");
  const [secret, setSecret] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (me) return <Navigate to="/portal" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(identifier.trim(), secret);
      navigate("/portal", { replace: true });
    } catch (err) {
      setError(errMsg(err, "Gagal masuk. Periksa koneksi Anda."));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m) => { setMode(m); setIdentifier(""); setSecret(""); setError(""); };

  return (
    <div className="relative flex min-h-screen items-center overflow-hidden bg-[#FFF5F3] px-5 py-12">
      <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-rose-100/80 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-10 h-80 w-80 rounded-full bg-orange-100/80 blur-3xl" />
      <form onSubmit={submit} className="fade-up relative mx-auto w-full max-w-sm space-y-5 rounded-2xl border border-gray-100 bg-white p-6 shadow-[0_10px_40px_-16px_rgba(16,24,40,0.18)] sm:p-8" data-testid="portal-login-form">
        <div className="flex flex-col items-center text-center">
          <CloverLogo className="h-14 w-14" testid="portal-login-logo" />
          <p className="mt-3 font-heading text-xs font-semibold uppercase tracking-[0.2em] text-primary">Clover Management</p>
          <h1 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-gray-900">Check-in Mandiri</h1>
          <p className="mt-1 text-sm text-gray-500">Untuk check-in kehadiran harian.</p>
        </div>
        <div className="grid grid-cols-2 rounded-full bg-gray-100 p-1">
          {[["hp", "No. HP + PIN"], ["email", "Email"]].map(([m, l]) => (
            <button key={m} type="button" data-testid={`portal-mode-${m}`} onClick={() => switchMode(m)}
              className={`h-10 rounded-full text-sm font-medium transition-colors ${mode === m ? "bg-white text-primary shadow-sm" : "text-gray-600"}`}>{l}</button>
          ))}
        </div>
        {mode === "hp" ? (
          <>
            <Field label="No. HP"><Input data-testid="portal-hp-input" type="tel" inputMode="tel" autoComplete="tel" required value={identifier} onChange={(e) => setIdentifier(e.target.value)} className="h-10 bg-white" placeholder="08xxxxxxxxxx" /></Field>
            <Field label="PIN (6 digit)">
              <Input data-testid="portal-pin-input" type="password" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={secret}
                onChange={(e) => setSecret(e.target.value.replace(/\D/g, "").slice(0, 6))} className="h-10 bg-white text-center font-mono text-2xl tracking-[0.5em]" />
            </Field>
          </>
        ) : (
          <>
            <Field label="Email"><Input data-testid="portal-email-input" type="email" autoComplete="email" required value={identifier} onChange={(e) => setIdentifier(e.target.value)} className="h-10 bg-white" /></Field>
            <Field label="Password"><Input data-testid="portal-password-input" type="password" autoComplete="current-password" required value={secret} onChange={(e) => setSecret(e.target.value)} className="h-10 bg-white" /></Field>
          </>
        )}
        {error && <p data-testid="portal-login-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}
        <Button data-testid="portal-login-submit" type="submit" disabled={loading} className="h-10 w-full rounded-full text-base font-semibold">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Masuk <ArrowRight className="ml-1 h-4 w-4" /></>}
        </Button>
        <p className="text-center text-sm text-gray-500">
          Admin? <Link to="/login" data-testid="portal-to-admin-login" className="font-semibold text-primary hover:underline">Masuk sebagai Admin</Link>
        </p>
      </form>
    </div>
  );
}
